import { Navigate, Outlet } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useTalentMapAccess } from '@/hooks/usePerformance';
import { WinclapLoader } from '@/components/ui/winclap-loader';

/**
 * Gate del Mapa de Talento. El acceso lo decide el governance config-driven
 * (`talentMap.checkAccess`): entra cualquier líder resuelto del community map,
 * el roster de People/HRBP y los admins. Así los líderes que no tienen rol
 * People/admin igual pueden evaluar a su equipo (doc secc. 6.1).
 *
 * Solo redirige cuando el server CONFIRMÓ que no hay acceso. Un error
 * transitorio de la query (retry: false) NO expulsa al usuario — muestra
 * un estado de reintento (antes un fallo de red en medio de la sesión te
 * sacaba del mapa sin explicación).
 */
export function TalentMapProtectedRoute() {
  const { t } = useTranslation();
  const { data: access, isLoading, isError, refetch, isRefetching } = useTalentMapAccess();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <WinclapLoader size={48} className="text-foreground/25" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-center">
        <p className="text-sm text-muted-foreground max-w-sm">
          {t(
            'performance.talent_map.access_error',
            'No pudimos verificar tu acceso al Mapa de Talento. Puede ser un problema momentáneo de conexión.'
          )}
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isRefetching}>
          <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${isRefetching ? 'animate-spin' : ''}`} />
          {t('common.ui.retry', 'Reintentar')}
        </Button>
      </div>
    );
  }

  if (!access?.canView) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
}
